import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, CheckCircle, XCircle, FileText, Download } from "lucide-react";
import { format } from "date-fns";
import { useState, useMemo } from "react";

type AppealStatus = "pending" | "approved" | "denied" | "withdrawn";

interface Appeal {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  status: AppealStatus;
  appealReason: string;
  createdAt: Date;
  suspensionReason?: string;
  evidence?: Array<{
    id: number;
    fileUrl: string;
    fileName: string;
    description?: string;
  }>;
}

export default function AdminAppeals() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<AppealStatus | "all">("pending");
  const [sortBy, setSortBy] = useState<"date" | "user" | "status">("date");
  const [selectedAppeals, setSelectedAppeals] = useState<Set<number>>(new Set());
  const [selectedAppeal, setSelectedAppeal] = useState<Appeal | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [reviewAction, setReviewAction] = useState<"approve" | "deny" | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [showBulkDenyModal, setShowBulkDenyModal] = useState(false);
  const [bulkReviewNotes, setBulkReviewNotes] = useState("");
  const itemsPerPage = 20;

  // Fetch appeals
  const { data: appealsData, isLoading: appealsLoading } = trpc.admin.appeals.getAllAppeals.useQuery(
    {
      status: statusFilter === "all" ? undefined : statusFilter,
      limit: 1000,
      offset: 0,
    },
    { placeholderData: { appeals: [], total: 0, limit: 1000, offset: 0 } }
  );

  // Fetch appeal stats
  const { data: statsData } = trpc.admin.appeals.getAppealStats.useQuery(
    undefined,
    { placeholderData: { total: 0, pending: 0, approved: 0, denied: 0, withdrawn: 0 } }
  );

  // Approve/Deny mutations
  const approveAppeal = trpc.admin.appeals.approveAppeal.useMutation({
    onSuccess: () => {
      setShowDetailModal(false);
      setReviewAction(null);
      setReviewNotes("");
    },
  });

  const denyAppeal = trpc.admin.appeals.denyAppeal.useMutation({
    onSuccess: () => {
      setShowDetailModal(false);
      setReviewAction(null);
      setReviewNotes("");
    },
  });

  // Bulk mutations
  const bulkApproveAppeals = trpc.admin.appeals.bulkApproveAppeals.useMutation({
    onSuccess: () => {
      setSelectedAppeals(new Set());
      setBulkReviewNotes("");
    },
  });

  const bulkDenyAppeals = trpc.admin.appeals.bulkDenyAppeals.useMutation({
    onSuccess: () => {
      setSelectedAppeals(new Set());
      setBulkReviewNotes("");
    },
  });

  // Filter and sort appeals
  const filteredAppeals = useMemo(() => {
    let filtered = (appealsData?.appeals || []) as unknown as Appeal[];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = (filtered as Appeal[]).filter(
        (appeal: Appeal) =>
          appeal.userName?.toLowerCase().includes(term) ||
          appeal.userEmail?.toLowerCase().includes(term) ||
          appeal.appealReason?.toLowerCase().includes(term)
      );
    }

    // Sort
    const sorted = [...(filtered as unknown as Appeal[])].sort((a: Appeal, b: Appeal) => {
      switch (sortBy) {
        case "user":
          return (a.userName || "").localeCompare(b.userName || "");
        case "status":
          return (a.status || "").localeCompare(b.status || "");
        case "date":
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

    return sorted;
  }, [appealsData?.appeals, searchTerm, sortBy]);

  // Pagination
  const paginatedAppeals = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAppeals.slice(start, start + itemsPerPage);
  }, [filteredAppeals, currentPage]);

  const totalPages = Math.ceil(filteredAppeals.length / itemsPerPage);

  // Bulk actions
  const handleSelectAll = () => {
    if (selectedAppeals.size === paginatedAppeals.length) {
      setSelectedAppeals(new Set());
    } else {
      const ids = (paginatedAppeals as unknown as Appeal[]).map((a) => a.id);
      setSelectedAppeals(new Set(ids));
    }
  };

  const toggleAppealSelection = (appealId: number) => {
    const newSelected = new Set(selectedAppeals);
    if (newSelected.has(appealId)) {
      newSelected.delete(appealId);
    } else {
      newSelected.add(appealId);
    }
    setSelectedAppeals(newSelected);
  };

  const handleBulkApprove = async () => {
    if (!bulkReviewNotes.trim()) {
      alert("Please enter review notes");
      return;
    }
    await bulkApproveAppeals.mutateAsync({
      appealIds: Array.from(selectedAppeals),
      reviewNotes: bulkReviewNotes,
    });
    setShowBulkApproveModal(false);
    setBulkReviewNotes("");
  };

  const handleBulkDeny = async () => {
    if (!bulkReviewNotes.trim()) {
      alert("Please enter denial reason");
      return;
    }
    await bulkDenyAppeals.mutateAsync({
      appealIds: Array.from(selectedAppeals),
      denialReason: bulkReviewNotes,
    });
    setShowBulkDenyModal(false);
    setBulkReviewNotes("");
  };

  const handleApproveAppeal = async () => {
    if (!selectedAppeal) return;
    await approveAppeal.mutateAsync({
      appealId: selectedAppeal.id,
      notes: reviewNotes || "Appeal approved",
    });
  };

  const handleDenyAppeal = async () => {
    if (!selectedAppeal) return;
    await denyAppeal.mutateAsync({
      appealId: selectedAppeal.id,
      reason: reviewNotes || "Appeal denied",
    });
  };

  const getStatusBadge = (status: AppealStatus) => {
    const variants: Record<AppealStatus, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "default",
      approved: "secondary",
      denied: "destructive",
      withdrawn: "outline",
    };

    const icons: Record<AppealStatus, React.ReactNode> = {
      pending: <AlertCircle className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      denied: <XCircle className="w-3 h-3" />,
      withdrawn: <AlertCircle className="w-3 h-3" />,
    };

    return (
      <Badge variant={variants[status]} className="flex items-center gap-1">
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Appeal Management</h1>
        <p className="text-muted-foreground">Review and manage user suspension appeals</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Appeals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsData?.total || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{statsData?.pending || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Approved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{statsData?.approved || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Denied</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{statsData?.denied || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Withdrawn</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{statsData?.withdrawn || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Search</label>
              <Input
                placeholder="Search by name, email, or reason..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="denied">Denied</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Sort By</label>
              <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Date (Newest)</SelectItem>
                  <SelectItem value="user">User Name</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("pending");
                  setSortBy("date");
                  setCurrentPage(1);
                }}
                className="w-full"
              >
                Reset Filters
              </Button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedAppeals.size > 0 && (
            <div className="flex gap-2 pt-4 border-t">
              <span className="text-sm font-medium text-muted-foreground">
                {selectedAppeals.size} selected
              </span>
              <Button
                size="sm"
                variant="default"
                onClick={handleBulkApprove}
                disabled={approveAppeal.isPending}
              >
                Approve Selected
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkDeny}
                disabled={denyAppeal.isPending}
              >
                Deny Selected
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedAppeals(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appeals Table */}
      <Card>
        <CardHeader>
          <CardTitle>Appeals ({filteredAppeals.length})</CardTitle>
          <CardDescription>
            Page {currentPage} of {totalPages || 1}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedAppeals.size === paginatedAppeals.length && paginatedAppeals.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appealsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading appeals...
                    </TableCell>
                  </TableRow>
                ) : paginatedAppeals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No appeals found
                    </TableCell>
                  </TableRow>
                ) : (
                  (paginatedAppeals as unknown as Appeal[]).map((appeal: Appeal) => (
                    <TableRow key={appeal.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedAppeals.has(appeal.id)}
                          onCheckedChange={() => toggleAppealSelection(appeal.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{appeal.userName}</TableCell>
                      <TableCell className="text-sm">{appeal.userEmail}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm">{appeal.appealReason}</TableCell>
                      <TableCell>{getStatusBadge(appeal.status)}</TableCell>
                      <TableCell className="text-sm">
                        {format(new Date(appeal.createdAt), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedAppeal(appeal);
                            setShowDetailModal(true);
                          }}
                        >
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, filteredAppeals.length)} of {filteredAppeals.length}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appeal Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Appeal Details</DialogTitle>
            <DialogDescription>
              Review appeal from {selectedAppeal?.userName}
            </DialogDescription>
          </DialogHeader>

          {selectedAppeal && (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="evidence">Evidence</TabsTrigger>
                <TabsTrigger value="review">Review</TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">User</label>
                    <p className="font-medium">{selectedAppeal.userName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="font-medium">{selectedAppeal.userEmail}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <p>{getStatusBadge(selectedAppeal.status)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Submitted</label>
                    <p className="font-medium">
                      {format(new Date(selectedAppeal.createdAt), "MMM d, yyyy HH:mm")}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Suspension Reason</label>
                  <p className="text-sm bg-muted p-3 rounded mt-1">
                    {selectedAppeal.suspensionReason || "No reason provided"}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Appeal Reason</label>
                  <p className="text-sm bg-muted p-3 rounded mt-1">{selectedAppeal.appealReason}</p>
                </div>
              </TabsContent>

              {/* Evidence Tab */}
              <TabsContent value="evidence" className="space-y-4">
                {selectedAppeal.evidence && selectedAppeal.evidence.length > 0 ? (
                  <div className="space-y-3">
                    {selectedAppeal.evidence.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <div>
                            <p className="font-medium text-sm">{file.fileName}</p>
                            {file.description && (
                              <p className="text-xs text-muted-foreground">{file.description}</p>
                            )}
                          </div>
                        </div>
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          <Download className="w-4 h-4" />
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No evidence provided</p>
                )}
              </TabsContent>

              {/* Review Tab */}
              <TabsContent value="review" className="space-y-4">
                {selectedAppeal.status === "pending" ? (
                  <>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Review Notes</label>
                      <Textarea
                        placeholder="Enter your review notes and decision reason..."
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        rows={4}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={handleApproveAppeal}
                        disabled={approveAppeal.isPending}
                        className="flex-1"
                      >
                        {approveAppeal.isPending ? "Approving..." : "Approve Appeal"}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDenyAppeal}
                        disabled={denyAppeal.isPending}
                        className="flex-1"
                      >
                        {denyAppeal.isPending ? "Denying..." : "Deny Appeal"}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm font-medium">
                      This appeal has already been {selectedAppeal.status}.
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
